import { DialogActionsBar } from '@progress/kendo-react-dialogs';
import { FC, useCallback } from 'react';
import { toast } from 'react-toastify';
import Button from 'src/components/design-system/Button';
import Modal from 'src/components/Modal';
import useAutoFocus from 'src/hooks/useAutoFocus';
import useBoolean from 'src/hooks/useBoolean';
import useCompany from 'src/hooks/useCompany';
import useInvoiceScanManager from 'src/hooks/useInvoiceScanManager';
import useDeleteOcrInvoiceAttachmentsApi from 'src/services/useDeleteOcrInvoiceAttachmentsApi';

interface DeleteConfirmationModalProps {
  onClose: () => void;
  visible: boolean;
  canDeleteAndMove: boolean;
  modalState: 'delete' | 'review' | 'failed';
  attachmentIds: number[];
}

const DeleteConfirmationModal: FC<DeleteConfirmationModalProps> = ({
  onClose,
  visible,
  canDeleteAndMove,
  modalState,
  attachmentIds,
}) => {
  const { company: selectedCompany } = useCompany();
  const { refreshPage, setRefreshCompany, getOcrRecordCount, closeReviewModal, enableRefresh } =
    useInvoiceScanManager();
  const { deleteOcrInvoiceAttachments } = useDeleteOcrInvoiceAttachmentsApi();
  const {
    value: deleteAndMoveLoading,
    setTrue: enableDeleteAndMoveLoading,
    setFalse: disableDeleteAndMoveLoading,
  } = useBoolean();
  const {
    value: deleteLoading,
    setTrue: enableDeleteLoading,
    setFalse: disableDeleteLoading,
  } = useBoolean();

  const handleClose = useCallback(() => {
    if (!deleteAndMoveLoading && !deleteLoading) {
      onClose();
      enableRefresh();
    }
  }, [deleteAndMoveLoading, deleteLoading, enableRefresh, onClose]);

  const handleDeleteAndMove = useCallback(
    async (moveAttachment: boolean) => {
      try {
        moveAttachment ? enableDeleteAndMoveLoading() : enableDeleteLoading();
        await deleteOcrInvoiceAttachments({
          attachmentIds: attachmentIds,
          moveAttachment: moveAttachment,
          companyId: Number(selectedCompany?.companyId),
        });
        if (moveAttachment) {
          toast.success('Deleted OCR record(s) and moved Image(s) to Records tab successfully');
        } else if (modalState === 'review' || modalState === 'failed') {
          toast.success('Deleted OCR record(s) and Image(s) Successfully');
        } else {
          toast.success('OCR Record(s) deleted successfully!');
        }
      } catch (error) {
        console.error(error);
      } finally {
        moveAttachment ? disableDeleteAndMoveLoading() : disableDeleteLoading();
        handleClose();
        closeReviewModal();
        refreshPage();
        getOcrRecordCount();
        !moveAttachment && setRefreshCompany?.((prev) => !prev);
      }
    },
    [
      enableDeleteAndMoveLoading,
      enableDeleteLoading,
      deleteOcrInvoiceAttachments,
      attachmentIds,
      selectedCompany?.companyId,
      disableDeleteAndMoveLoading,
      disableDeleteLoading,
      handleClose,
      closeReviewModal,
      modalState,
      refreshPage,
      getOcrRecordCount,
      setRefreshCompany,
    ]
  );

  const deleteModalTitle = canDeleteAndMove ? 'Deletion Options' : 'Delete Confirmation';
  const confirmButtonRef = useAutoFocus(visible);

  return (
    <Modal title={deleteModalTitle} closeModal={handleClose} visible={visible} width="630px">
      {!canDeleteAndMove ? (
        <div className="flex flex-col gap-2 text-sm">
          <div className="flex flex-col gap-2 ">
            {modalState === 'review' && (
              <div>
                The doc # provided is a match with an existing document created via the OCR Service.
                As a result, you cannot create a duplicate or attach this image file to any other
                documents.
              </div>
            )}
            {(modalState === 'review' || modalState === 'failed') && (
              <div>
                Please confirm if you would like to proceed with removing this record from the OCR
                queue and deleting the associated image file.
              </div>
            )}
            {modalState === 'delete' && (
              <div>
                Please confirm if you want to delete the selected OCR record(s) and associated image
                file(s).
              </div>
            )}
          </div>
          <DialogActionsBar>
            <div className="flex flex-row gap-1 justify-end items-center mt-1">
              <Button
                color="info"
                onClick={() => handleDeleteAndMove(false)}
                loading={deleteLoading}
                ref={confirmButtonRef}
              >
                {modalState === 'failed' ? 'Delete OCR and Delete Image' : 'Confirm Delete'}
              </Button>
              <Button color="info" onClick={handleClose} disabled={deleteLoading}>
                Cancel
              </Button>
            </div>
          </DialogActionsBar>
        </div>
      ) : (
        <div className="flex flex-col gap-2 text-sm">
          <div>
            {modalState === 'review' &&
              'Please confirm if you would like to delete the OCR record and delete the image file from Scan Manager.'}
            {modalState === 'failed' &&
              'You can delete both the Attachment and OCR Record from Scan Manager.'}
          </div>
          <div>OR</div>
          <div>
            {modalState === 'review' &&
              'Delete the OCR record, but move the image file to the records queue.'}
            {modalState === 'failed' && 'You can keep the Attachment and delete the OCR Record.'}
          </div>
          <DialogActionsBar>
            <div className="flex flex-row flex-wrap justify-end items-center mt-2 ml-[-10px]">
              <Button
                color="info"
                onClick={() => handleDeleteAndMove(false)}
                loading={deleteLoading}
                disabled={deleteAndMoveLoading}
                ref={confirmButtonRef}
              >
                {modalState === 'review' && 'Delete OCR and Delete Image'}
                {modalState === 'failed' && 'Delete both Attachment and OCR Record'}
              </Button>
              <Button
                color="info"
                onClick={() => handleDeleteAndMove(true)}
                loading={deleteAndMoveLoading}
                disabled={deleteLoading}
              >
                {modalState === 'review' && 'Delete OCR but move Image'}
                {modalState === 'failed' && 'Keep Attachment only'}
              </Button>
              <Button
                onClick={handleClose}
                color="info"
                disabled={deleteAndMoveLoading || deleteLoading}
              >
                Cancel
              </Button>
            </div>
          </DialogActionsBar>
        </div>
      )}
    </Modal>
  );
};

export default DeleteConfirmationModal;
